import React from 'react';
import Grid from 'src/app/atoms/Layout/Grid/Grid';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import Typography, { TypographyProps } from 'src/app/atoms/Typography/Typography';
import TED from 'src/app/icons/Custom/Logos/SocialProof/TED';
import KonMari from 'src/app/icons/Custom/Logos/SocialProof/KonMari';
import MatthewHussey from 'src/app/icons/Custom/Logos/SocialProof/MatthewHussey';
import MelRobbins from 'src/app/icons/Custom/Logos/SocialProof/MelRobbins';
import BODi from 'src/app/icons/Custom/Logos/SocialProof/BODi';
import Athletic from 'src/app/icons/Custom/Logos/SocialProof/Athletic';
import Mindbody from 'src/app/icons/Custom/Logos/SocialProof/Mindbody';
import Budgetnista from 'src/app/icons/Custom/Logos/SocialProof/Budgetnista';
import MarieForleo from 'src/app/icons/Custom/Logos/SocialProof/MarieForleo';
import Keap from 'src/app/icons/Custom/Logos/SocialProof/Keap';
import MarkHyman from 'src/app/icons/Custom/Logos/SocialProof/MarkHyman';
import TheHomeEdit from 'src/app/icons/Custom/Logos/SocialProof/TheHomeEdit';
import DailyStoic from 'src/app/icons/Custom/Logos/SocialProof/DailyStoic';
import HadarShemesh from 'src/app/icons/Custom/Logos/SocialProof/HadarShemesh';
import Whole30 from 'src/app/icons/Custom/Logos/SocialProof/Whole30';
import BecomingMinimalist from 'src/app/icons/Custom/Logos/SocialProof/BecomingMinimalist';
import JimKwik from 'src/app/icons/Custom/Logos/SocialProof/JimKwik';
import CreateTheLove from 'src/app/icons/Custom/Logos/SocialProof/CreateTheLove';
import Jefferson from 'src/app/icons/Custom/Logos/SocialProof/Jefferson';
import Luvvie from 'src/app/icons/Custom/Logos/SocialProof/Luvvie';
import Cambridge from 'src/app/icons/Custom/Logos/SocialProof/Cambridge';
import { cssVar } from 'src/app/styles/helper';
import { GradientLeft, GradientRight, LogosContainer, LogosRail } from './CompanyLogoSet.styles';
import { LogoIconProps } from 'app/icons/Custom/Logos/logo';

export interface CompanyLogoSetProps {
  title?: TypographyProps;
  theme?: 'default' | 'brand';
  customGradientColor?: string;
  customLogoSet?: ((props: LogoIconProps) => JSX.Element)[];
  duration?: number;
}

const COLORS_MAP = {
  default: {
    color: cssVar('brand-950'),
    opacity: 0.5,
  },
  brand: {
    color: cssVar('base-white'),
    opacity: 1,
  },
};

const CompanyLogoSet = ({
  title,
  theme = 'default',
  customGradientColor = '#000000',
  customLogoSet,
  duration = 120,
}: CompanyLogoSetProps): JSX.Element => {
  const logos = customLogoSet ?? [
    TED,
    KonMari,
    MatthewHussey,
    MelRobbins,
    BODi,
    Athletic,
    Mindbody,
    Budgetnista,
    MarieForleo,
    Keap,
    MarkHyman,
    TheHomeEdit,
    DailyStoic,
    HadarShemesh,
    Whole30,
    BecomingMinimalist,
    JimKwik,
    CreateTheLove,
    Jefferson,
    Luvvie,
    Cambridge,
  ];
  const colors = COLORS_MAP[theme];

  // Make 4x the length of the logos array so it does not break in bigger screens
  const fullLogoRow = [...logos, ...logos, ...logos, ...logos];

  const logosFirstHalf = [...logos].slice(0, logos.length / 2);
  const logosSecondHalf = [...logos].slice(logos.length / 2);

  const duplicatedLogosFirstHalf = [...logosFirstHalf, ...logosFirstHalf];
  const duplicatedLogosSecondHalf = [...logosSecondHalf, ...logosSecondHalf];

  const renderRow = (logoSet: typeof logos, reverse = false, durationTime = duration) => (
    <LogosRail
      $reverse={reverse}
      $duration={durationTime}
    >
      {logoSet.map((Logo, index) => (
        <Logo
          key={index}
          color={colors.color}
          opacity={colors.opacity}
          className='logo'
        />
      ))}
    </LogosRail>
  );

  return (
    <Grid>
      <Stack
        direction='column'
        gap='spacing-4xl'
        overflow='hidden'
        position='relative'
      >
        {title && <Typography {...title} />}
        <LogosContainer
          position='relative'
          hideOn='sm'
        >
          <GradientLeft
            theme={theme}
            customGradientColor={customGradientColor}
          />
          <GradientRight
            theme={theme}
            customGradientColor={customGradientColor}
          />
          {renderRow(fullLogoRow)}
        </LogosContainer>

        <Grid
          showOn='sm'
          gap='spacing-xl'
          gapSm='spacing-4xl'
          totalColumns={1}
        >
          <GradientLeft
            theme={theme}
            customGradientColor={customGradientColor}
          />
          <GradientRight
            theme={theme}
            customGradientColor={customGradientColor}
          />
          <LogosContainer position='relative'>
            {renderRow(duplicatedLogosFirstHalf, false, duration / 4)}
          </LogosContainer>
          <LogosContainer position='relative'>
            {renderRow(duplicatedLogosSecondHalf, true, duration / 4)}
          </LogosContainer>
        </Grid>
      </Stack>
    </Grid>
  );
};

export default CompanyLogoSet;
